/* eslint-disable quotes */
import * as React from "react";
import dayjs from "dayjs";
import parse from "html-react-parser";

import { withJsonFormsControlProps } from "@jsonforms/react";
import { ControlElement, ControlProps } from "@jsonforms/core";

import FormField from "@ingka/form-field";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FormHelperText, InputLabel } from "@mui/material";

import { datePickerInputFieldStyles } from "../../styles";
import { findAndTransformHTMLString } from "../utils";

interface InputProps extends ControlProps {
	uischema: {
		subText?: string;
	} & ControlElement;
}

const DateTimePickerControl: React.FC<InputProps> = ({
	data,
	handleChange,
	path,
	label,
	enabled,
	description,
	uischema,
	errors,
	required,
}) => {
	const onChange = React.useCallback(
		(val: dayjs.Dayjs | null) => {
			handleChange(path, dayjs(val).format("YYYY-MM-DDTHH:mm:00Z").toString());
		},
		[path, handleChange]
	);

	const errMessage =
		required && errors.length
			? `${label} ${errors}`
			: required
				? `${label} is a required property`
				: `${label} ${errors}`;

	const dateTimePicker = React.useMemo(
		() => (
			<LocalizationProvider dateAdapter={AdapterDayjs}>
				<InputLabel
					htmlFor="date-input"
					sx={{
						fontSize: "0.875rem",
						lineHeight: "1.571",
						color: "rgb(72, 72, 72)",
						letterSpacing: "0",
						// eslint-disable-next-line quotes
						fontFamily: `"Noto IKEA", "Noto Sans", "Roboto", "Open Sans", "sans-serif"`,
					}}
				>
					{label}
				</InputLabel>
				<DateTimePicker
					label=""
					disabled={!enabled}
					value={data ? dayjs(data) : null}
					format="YYYY-MM-DD HH:mm"
					onChange={(newValue) => onChange(newValue)}
					slotProps={{
						textField: {
							inputProps: {
								"aria-label": label,
								"aria-describedby": `input-control-msg-${path}`,
								id: "date-input",
							},
							variant: "outlined",
							sx: {
								...datePickerInputFieldStyles,
								fieldset: {
									margin: 0,
								},
							},
						},
					}}
				/>
			</LocalizationProvider>
		),
		[label, enabled, data, path, onChange]
	);
	return (
		<>
			<FormField
				fieldHelper={{
					msg: (
						<>
							Time is selected in local timezone
							<br />
							{uischema?.subText && (
								<>
									{parse(findAndTransformHTMLString(uischema.subText))}
									<br />
								</>
							)}
							{description && (
								<>
									{description}
									<br />
								</>
							)}
						</>
					),
					id: `input-control-msg-${path}`,
				}}
				valid={required ? !errors && data : !errors}
				shouldValidate={errors.length > 0 || (required && !data)}
				validation={{
					id: "error-msg-id",
					msg: errMessage,
					type: "error",
				}}
			>
				{dateTimePicker}
			</FormField>
		</>
	);
};

export default withJsonFormsControlProps(DateTimePickerControl);
